import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import "./game.css";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getDvSourceImageUrl } from "../../functions/themeAssets";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import {
  CORNERS,
  allGoodItemsDone,
  buildInitialSlots,
  formatMmSs,
  idleTierForScore,
  idleVideoKey,
  isImageMediaUrl,
  parseHeartItems,
  parseThemeBool,
  pointInRect,
  refillBoard,
  resolveThemeBackdropKey,
  shuffleArray,
} from "./heartGameUtils";

const USER_DATA_KEY = "userData";

function readSessionUserData() {
  try {
    return JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function readSessionReportId() {
  try {
    const u = JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}");
    const id = u?.reportId ?? u?.report_id;
    return id != null && String(id).trim() !== "" ? String(id).trim() : null;
  } catch {
    return null;
  }
}

function patchSessionReportId(reportId) {
  try {
    const raw = sessionStorage.getItem(USER_DATA_KEY);
    const u = raw ? JSON.parse(raw) : {};
    if (!reportId) return;
    sessionStorage.setItem(USER_DATA_KEY, JSON.stringify({ ...u, reportId: String(reportId) }));
  } catch (_) {}
}

function resolveReportsRole(ud) {
  const r = String(ud?.role || "").trim();
  const s = String(ud?.source || "").trim().toUpperCase();
  if (r.toLowerCase() === "demobypass") return "demobypass";
  if (r === "GUEST_USER" || s === "GUEST_USER") return "GUEST_USER";
  return "ORG_USER";
}

function stageUserTypeLabel(ud) {
  const rr = resolveReportsRole(ud);
  if (rr === "demobypass") return "DEMO_USER";
  if (rr === "GUEST_USER") return "GUEST_USER";
  return "ORG_USER";
}

function CloudItem({ item, cloudUrl, showName }) {
  const contentUrl =
    item.type !== "text" && item.contentFilename
      ? getDvSourceImageUrl(item.contentFilename)
      : "";

  return (
    <div className={`hs-cloud-bundle${!cloudUrl ? " hs-cloud-bundle--fallback" : ""}`}>
      {cloudUrl ? <img src={cloudUrl} alt="" className="hs-cloud-img" draggable={false} /> : null}
      <div className="hs-cloud-item-center">
        {contentUrl ? (
          <img src={contentUrl} alt={item.name} className="hs-cloud-item-media" draggable={false} />
        ) : (
          <span className="hs-cloud-item-text">{item.name}</span>
        )}
        {showName && contentUrl && (
          <span className="hs-cloud-item-label">{item.name}</span>
        )}
      </div>
    </div>
  );
}

/** Drag ghost — only the item itself follows the pointer, the cloud graphic stays put. */
function DragItemGhost({ item }) {
  const contentUrl =
    item.type !== "text" && item.contentFilename
      ? getDvSourceImageUrl(item.contentFilename)
      : "";

  return (
    <div className="hs-drag-item-ghost">
      {contentUrl ? (
        <img src={contentUrl} alt={item.name} className="hs-cloud-item-media" draggable={false} />
      ) : (
        <span className="hs-cloud-item-text">{item.name}</span>
      )}
    </div>
  );
}

export default function GamePage() {
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData, status: themeStatus } = useSelector((state) => state.theme);

  const storedUserData = useMemo(() => readSessionUserData(), []);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL ?? "").replace(/\/+$/, "");
  const userId = storedUserData?.userId || storedUserData?.userid || storedUserData?.id || "";
  const currentTheme = themeData?.themename ?? themeData?.themeName ?? "";

  const [narrowViewport, setNarrowViewport] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);

  const [score, setScore] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [pointsFlash, setPointsFlash] = useState(null);
  const [wrongTotal, setWrongTotal] = useState(0);
  const [dropsCount, setDropsCount] = useState(0);

  const [slots, setSlots] = useState(() => CORNERS.map((corner) => ({ corner, item: null })));
  const goodQueueRef = useRef([]);
  const badQueueRef = useRef([]);

  const [drag, setDrag] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mascotMode, setMascotMode] = useState("idle");
  const [idleTier, setIdleTier] = useState("mid");
  const [actionVideoSrc, setActionVideoSrc] = useState("");
  const [actionText, setActionText] = useState("");
  const [didYouKnow, setDidYouKnow] = useState("");
  const pendingDescRef = useRef("");

  const mascotRef = useRef(null);
  const videoRef = useRef(null);
  const stageIdRef = useRef(null);
  const elapsedRef = useRef(0);
  const reportIdRef = useRef(readSessionReportId());
  const scoreRef = useRef(0);
  const slotsRef = useRef(slots);
  const wrongTotalRef = useRef(0);
  const dropsCountRef = useRef(0);
  const gameOverRef = useRef(false);
  const dropLogRef = useRef([]);
  const pendingFinishRef = useRef(false);

  const rightPoints = useMemo(() => {
    const n = parseInt(String(themeData?.points ?? "1"), 10);
    return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 1;
  }, [themeData?.points]);

  const wrongPoints = useMemo(() => {
    const n = parseInt(String(themeData?.wrong_points ?? "0"), 10);
    return Number.isFinite(n) ? Math.min(0, Math.max(-50, n)) : 0;
  }, [themeData?.wrong_points]);

  const goodShowName = parseThemeBool(themeData?.good_show_item_name, false);
  const badShowName = parseThemeBool(themeData?.bad_show_item_name, false);
  const goodShowDesc = parseThemeBool(themeData?.good_show_description, false);
  const badShowDesc = parseThemeBool(themeData?.bad_show_description, false);

  const bgUrl = useMemo(() => {
    const key = resolveThemeBackdropKey(themeData, narrowViewport);
    return key ? getDvSourceImageUrl(key) : "";
  }, [themeData, narrowViewport]);

  const cloudUrl = useMemo(() => {
    const key = String(themeData?.cloud_image ?? "").trim();
    return key ? getDvSourceImageUrl(key) : "";
  }, [themeData?.cloud_image]);

  const idleVideoUrl = useMemo(() => {
    const key = String(themeData?.[idleVideoKey(idleTier)] ?? "").trim();
    return key ? getDvSourceImageUrl(key) : "";
  }, [themeData, idleTier]);

  const boxColor = String(themeData?.box_color ?? "#c62828");
  const textColor = String(themeData?.textcolor ?? "#ffffff");
  const landingTitle = String(themeData?.landing_page_title ?? "").trim();

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    slotsRef.current = slots;
  }, [slots]);

  useEffect(() => {
    wrongTotalRef.current = wrongTotal;
  }, [wrongTotal]);

  useEffect(() => {
    dropsCountRef.current = dropsCount;
  }, [dropsCount]);

  useEffect(() => {
    elapsedRef.current = elapsed;
  }, [elapsed]);

  useEffect(() => {
    const header = document.querySelector(".upperaction");
    if (!header) return;
    const setHeaderVar = () => {
      document.documentElement.style.setProperty("--hs-header-h", `${header.offsetHeight}px`);
    };
    setHeaderVar();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(setHeaderVar) : null;
    ro?.observe(header);
    window.addEventListener("resize", setHeaderVar);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", setHeaderVar);
      document.documentElement.style.removeProperty("--hs-header-h");
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const sync = () => setNarrowViewport(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync);
    sync();
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
    };
  }, []);

  useEffect(() => {
    if (!themeData) return;
    const t = setInterval(() => setElapsed((p) => p + 1), 1000);
    return () => clearInterval(t);
  }, [themeData]);

  const showPointsDelta = (delta) => {
    setPointsFlash({ key: Date.now(), delta });
    window.setTimeout(() => setPointsFlash(null), 900);
  };

  const syncExternalReport = useCallback(
    async (scoreSnapshot, timeSnapshot) => {
      const ud = storedUserData;
      const rr = resolveReportsRole(ud);
      if (rr === "demobypass") return;
      let fresh;
      try {
        fresh = JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}");
      } catch {
        fresh = ud;
      }
      const sidToken = fresh?.token ?? ud?.token ?? "";
      const reportBase = String(process.env.REACT_APP_REPORTS_BASE_URL ?? "").replace(/\/+$/, "");
      if (!reportBase || !fresh?.sessionId || !fresh?.organizationId || !fresh?.userId) return;
      const ridStored = reportIdRef.current ?? readSessionReportId();
      try {
        const payload = {
          sessionId: fresh.sessionId,
          organizationId: fresh.organizationId,
          userId: fresh.userId,
          role: rr === "GUEST_USER" ? "GUEST_USER" : "ORG_USER",
          token: sidToken || "",
          points: Number(scoreSnapshot) || 0,
          // Same value the leaderboard reads from `stat.time` — no separate client-side timer here.
          time: timeSnapshot ?? formatMmSs(elapsedRef.current),
          gameId: fresh.gameId ?? ud?.gameId ?? null,
          name: fresh.name ?? ud?.name ?? "",
        };
        if (ridStored) payload.reportId = ridStored;
        const resPayload = await sendReport(payload);
        const nextRid =
          extractReportIdFromResponse(resPayload) ||
          extractReportIdFromResponse(resPayload?.data ?? resPayload);
        if (nextRid) {
          reportIdRef.current = nextRid;
          patchSessionReportId(nextRid);
          const stageId = stageIdRef.current;
          if (backendBase && adminToken && stageId != null) {
            try {
              await fetch(`${backendBase}/welcomeStageLinkReport`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
                body: JSON.stringify({
                  userId: fresh.userId,
                  stageId,
                  id: stageId,
                  sessionId: fresh.sessionId,
                  organizationId: fresh.organizationId,
                  reportId: nextRid,
                }),
              });
            } catch (e) {
              console.warn("welcomeStageLinkReport failed:", e?.message ?? e);
            }
          }
        }
      } catch (e) {
        console.warn("sendReport failed:", e?.message ?? e);
      }
    },
    [storedUserData, backendBase, adminToken]
  );

  const persistProgress = useCallback(
    async (scoreVal, wrongcount, currentQues) => {
      const stageId = stageIdRef.current;
      if (!backendBase || !adminToken || !userId || stageId == null) return;
      try {
        await fetch(`${backendBase}/welcomeStageProgress`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({
            userId,
            stageId,
            id: stageId,
            time: formatMmSs(elapsedRef.current),
            total_score: scoreVal,
            wrongcount,
            currentQues,
            answer: JSON.stringify(dropLogRef.current),
          }),
        });
      } catch (e) {
        console.warn("welcomeStageProgress error", e);
      }
    },
    [adminToken, backendBase, userId]
  );

  const finishGame = useCallback(async () => {
    if (gameOverRef.current) return;
    gameOverRef.current = true;
    const stageId = stageIdRef.current;
    const finalScore = scoreRef.current;
    let dbTime = null;
    if (backendBase && adminToken && userId && stageId != null) {
      try {
        const resp = await fetch(`${backendBase}/welcomeStageComplete`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({
            userId,
            stageId,
            id: stageId,
            time: formatMmSs(elapsedRef.current),
            total_score: finalScore,
            wrongcount: wrongTotalRef.current,
            currentQues: dropsCountRef.current,
          }),
        });
        const body = await resp.json().catch(() => ({}));
        dbTime = body?.time ?? null;
      } catch (e) {
        console.warn("welcomeStageComplete error", e);
      }
    }
    // Same value the leaderboard will show — read back from `stat` rather than the local timer.
    await syncExternalReport(finalScore, dbTime ?? formatMmSs(elapsedRef.current));
    window.location.assign("/leaderboard");
  }, [adminToken, backendBase, syncExternalReport, userId]);

  const playIdleForScore = useCallback((nextScore) => {
    setMascotMode("idle");
    setActionVideoSrc("");
    setActionText("");
    setIdleTier(idleTierForScore(nextScore));
  }, []);

  const refillSlot = useCallback((prevSlots, corner) => {
    return refillBoard(prevSlots, corner, goodQueueRef.current, badQueueRef.current);
  }, []);

  const handleDrop = useCallback(
    async (corner) => {
      if (busy || gameOverRef.current) return;
      const slot = slots.find((s) => s.corner === corner);
      const item = slot?.item;
      if (!item) return;

      setBusy(true);
      const delta = item.isGood ? rightPoints : wrongPoints;
      const nextScore = scoreRef.current + delta;
      const nextWrong = item.isGood ? wrongTotal : wrongTotal + 1;

      setScore(nextScore);
      if (!item.isGood) {
        wrongTotalRef.current = nextWrong;
        setWrongTotal(nextWrong);
      }
      setDropsCount((c) => {
        const n = c + 1;
        dropsCountRef.current = n;
        return n;
      });
      showPointsDelta(delta);

      dropLogRef.current.push({
        name: item.name,
        isGood: item.isGood,
        delta,
        score: nextScore,
        at: formatMmSs(elapsedRef.current),
      });

      const nextSlots = refillSlot(slots, corner);
      slotsRef.current = nextSlots;
      setSlots(nextSlots);

      pendingFinishRef.current = allGoodItemsDone(goodQueueRef.current, nextSlots);

      const showDesc = item.isGood ? goodShowDesc : badShowDesc;
      pendingDescRef.current = showDesc && item.description ? item.description : "";

      const isTextMascot = item.mascotType === "text";
      const mascotSrc = !isTextMascot && item.mascotFilename ? getDvSourceImageUrl(item.mascotFilename) : "";
      if (isTextMascot && item.mascotText) {
        setMascotMode("action");
        setActionVideoSrc("");
        setActionText(item.mascotText);
      } else if (mascotSrc) {
        setMascotMode("action");
        setActionText("");
        setActionVideoSrc(mascotSrc);
      } else {
        playIdleForScore(nextScore);
        if (pendingDescRef.current) {
          setDidYouKnow(pendingDescRef.current);
          return;
        }
        setBusy(false);
        await persistProgress(nextScore, nextWrong, dropsCountRef.current);
        if (pendingFinishRef.current) await finishGame();
      }
    },
    [busy, finishGame, persistProgress, playIdleForScore, refillSlot, rightPoints, slots, wrongPoints, wrongTotal, goodShowDesc, badShowDesc]
  );

  const onMascotVideoEnded = useCallback(async () => {
    const nextScore = scoreRef.current;
    playIdleForScore(nextScore);
    if (pendingDescRef.current) {
      setDidYouKnow(pendingDescRef.current);
      return;
    }
    setBusy(false);
    await persistProgress(nextScore, wrongTotalRef.current, dropsCountRef.current);
    if (pendingFinishRef.current) await finishGame();
  }, [finishGame, persistProgress, playIdleForScore]);

  const closeDidYouKnow = useCallback(async () => {
    pendingDescRef.current = "";
    setDidYouKnow("");
    setBusy(false);
    await persistProgress(scoreRef.current, wrongTotalRef.current, dropsCountRef.current);
    if (pendingFinishRef.current) await finishGame();
  }, [finishGame, persistProgress]);

  const currentMascotSrc = mascotMode === "action" ? actionVideoSrc : idleVideoUrl;
  const currentMascotIsImage = isImageMediaUrl(currentMascotSrc);

  useEffect(() => {
    if (currentMascotIsImage) return;
    const v = videoRef.current;
    if (!v) return;
    if (mascotMode === "action" && actionVideoSrc) {
      v.src = actionVideoSrc;
      v.loop = false;
      v.play().catch(() => {});
    } else if (mascotMode === "idle" && idleVideoUrl) {
      v.src = idleVideoUrl;
      v.loop = true;
      v.play().catch(() => {});
    }
  }, [mascotMode, actionVideoSrc, idleVideoUrl, currentMascotIsImage]);

  /** PNG/GIF action mascots have no onEnded — hold briefly then return to idle */
  useEffect(() => {
    if (mascotMode !== "action") return;
    const isTextAction = !!actionText && !actionVideoSrc;
    if (!isTextAction && (!actionVideoSrc || !isImageMediaUrl(actionVideoSrc))) return;
    const t = window.setTimeout(() => onMascotVideoEnded(), 2800);
    return () => window.clearTimeout(t);
  }, [mascotMode, actionVideoSrc, actionText, onMascotVideoEnded]);

  useEffect(() => {
    if (themeStatus === "loading" || !themeData) return;

    const good = shuffleArray(parseHeartItems(themeData).filter((it) => it.isGood));
    const bad = shuffleArray(parseHeartItems(themeData).filter((it) => !it.isGood));

    if (good.length < 2 || bad.length < 2) {
      setLoadError("Need at least 2 good and 2 bad items in theme settings.");
      setLoading(false);
      return;
    }
    setLoadError("");

    goodQueueRef.current = [...good];
    badQueueRef.current = [...bad];
    setSlots(buildInitialSlots(goodQueueRef.current, badQueueRef.current));
    setIdleTier("mid");
    setMascotMode("idle");
    setDidYouKnow("");
    pendingDescRef.current = "";
    setScore(0);
    setWrongTotal(0);
    setDropsCount(0);
    dropLogRef.current = [];
    gameOverRef.current = false;

    (async () => {
      if (backendBase && adminToken && userId) {
        try {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
            body: JSON.stringify({
              userId,
              email: storedUserData?.email ?? "",
              empId: storedUserData?.employeeId ?? "",
              name: storedUserData?.name ?? "",
              user_type: stageUserTypeLabel(storedUserData),
              themename: currentTheme,
              themeName: currentTheme,
              currentTheme,
              questions: good.map((it) => it.sortOrder),
            }),
          });
          const body = await startRes.json().catch(() => ({}));
          stageIdRef.current = body?.id ?? null;
          if (Number(body?.progress?.gameover) === 1) {
            window.location.replace("/leaderboard");
            return;
          }
        } catch (e) {
          console.warn("welcomeStageStart error", e);
        }
      }
      setLoading(false);
    })();
  }, [adminToken, backendBase, currentTheme, storedUserData, themeData, themeStatus, userId]);

  const onPointerDown = (corner, e) => {
    if (busy || gameOverRef.current || drag) return;
    const slot = slots.find((s) => s.corner === corner);
    if (!slot?.item) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({
      corner,
      x: e.clientX,
      y: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      w: rect.width,
      h: rect.height,
    });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : null));
  };

  const onPointerUp = (e) => {
    if (!drag) return;
    const rect = mascotRef.current?.getBoundingClientRect();
    const hit = pointInRect(e.clientX, e.clientY, rect);
    const corner = drag.corner;
    setDrag(null);
    if (hit) handleDrop(corner);
  };

  const rules = Array.isArray(themeData?.rules) ? themeData.rules : [];

  if (themeStatus === "loading" || loading) {
    return (
      <div className="hs-game-loading">
        <div className="hs-game-spinner" />
        <p>Loading game…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="hs-game-loading">
        <p>{loadError}</p>
      </div>
    );
  }

  const dragSlot = drag ? slots.find((s) => s.corner === drag.corner) : null;

  return (
    <div
      className="hs-game"
      style={{ "--hs-box-color": boxColor, "--hs-text-color": textColor }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {bgUrl ? <div className="hs-game-bg" style={{ backgroundImage: `url("${bgUrl}")` }} /> : null}

      <header className="hs-game-hud">
        <div className="hs-hud-left">
          <button type="button" className="hs-hud-pill hs-hud-help" onClick={() => setRulesOpen(true)} aria-label="Rules">
            ?
          </button>
          <div className="hs-hud-pill hs-hud-timer">
            <span className="hs-hud-icon" aria-hidden>⏱</span>
            <span>{formatMmSs(elapsed)}</span>
          </div>
        </div>

        {landingTitle ? <div className="hs-hud-pill hs-hud-title">{landingTitle}</div> : null}

        <div className="hs-hud-pill hs-hud-score">
          <i className="fa-solid fa-heart-pulse hs-hud-heart" aria-hidden="true" />
          <span>{score}</span>
          {pointsFlash && (
            <span
              key={pointsFlash.key}
              className={`hs-points-flash${pointsFlash.delta >= 0 ? " hs-points-flash--up" : " hs-points-flash--down"}`}
            >
              {pointsFlash.delta > 0 ? `+${pointsFlash.delta}` : pointsFlash.delta}
            </span>
          )}
        </div>
      </header>

      {CORNERS.map((corner) => {
        const slot = slots.find((s) => s.corner === corner);
        const item = slot?.item;
        if (!item) {
          return <div key={corner} className={`hs-cloud-anchor hs-cloud-anchor--${corner}`} />;
        }
        const isDragSource = drag?.corner === corner;
        const showName = item.isGood ? goodShowName : badShowName;
        return (
          <div
            key={corner}
            className={`hs-cloud-anchor hs-cloud-anchor--${corner}${busy ? " hs-cloud-anchor--disabled" : ""}${isDragSource ? " hs-cloud-anchor--source" : ""}`}
            onPointerDown={(e) => onPointerDown(corner, e)}
          >
            <CloudItem item={item} cloudUrl={cloudUrl} showName={showName} />
          </div>
        );
      })}

      {drag && dragSlot?.item && (
        <div
          className="hs-cloud-drag-layer"
          style={{
            left: drag.x - drag.offsetX,
            top: drag.y - drag.offsetY,
            width: drag.w,
            height: drag.h,
          }}
        >
          <DragItemGhost item={dragSlot.item} />
        </div>
      )}

      <div ref={mascotRef} className={`hs-mascot-zone${busy ? " hs-mascot-zone--active" : ""}`}>
        {mascotMode === "action" && actionText && !actionVideoSrc ? (
          <div className="hs-mascot-text">{actionText}</div>
        ) : currentMascotSrc ? (
          currentMascotIsImage ? (
            <img src={currentMascotSrc} alt="" className="hs-mascot-img" draggable={false} />
          ) : (
            <video
              ref={videoRef}
              className="hs-mascot-video"
              playsInline
              muted
              onEnded={mascotMode === "action" ? onMascotVideoEnded : undefined}
            />
          )
        ) : (
          <div className="hs-mascot-placeholder">♥</div>
        )}
      </div>

      {didYouKnow && (
        <div className="hs-rules-overlay" role="dialog" aria-modal="true">
          <div className={`hs-rules-card${cloudUrl ? " hs-rules-card--cloud" : ""}`}>
            {cloudUrl ? (
              <img src={cloudUrl} alt="" className="hs-rules-cloud-img" draggable={false} />
            ) : null}
            <div className="hs-rules-content">
              <h2>Did you Know?</h2>
              <p className="hs-dyk-text">{didYouKnow}</p>
              <button type="button" className="hs-rules-close" onClick={closeDidYouKnow}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {rulesOpen && (
        <div className="hs-rules-overlay" role="dialog" aria-modal="true">
          <div className={`hs-rules-card${cloudUrl ? " hs-rules-card--cloud" : ""}`}>
            {cloudUrl ? (
              <img src={cloudUrl} alt="" className="hs-rules-cloud-img" draggable={false} />
            ) : null}
            <div className="hs-rules-content">
              <h2>How to Play</h2>
              <ul>
                {rules.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
              <button type="button" className="hs-rules-close" onClick={() => setRulesOpen(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
